import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, Skeleton } from '@mui/material';

import PaymentHistoryCard from './PaymentHistoryCard';
import { PaymentHistoryCardSkeleton } from './PaymentHistoryCard';

import { useInfinitePaymentList } from '../../../../hooks/queries/usePaymentList';
import { useAppSelector } from '../../../../redux/hooks';

import { ROUTES, PROFILE_ROUTES } from '../../../../constants/paths';

const { PROFILE } = ROUTES;
const { PAYMENT_DETAILS } = PROFILE_ROUTES;

const PaymentHistoryListingComponent = () => {
  const { id: patientId } =
    useAppSelector((state) => state.auth.currentUser) || {};

  const {
    payments: paymentList,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    totalRecords,
  } = useInfinitePaymentList({ patientId });

  const navigate = useNavigate();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const handlePaymentClick = (payment: {
    id: number;
    description: string;
    amount: string;
    status: string;
  }) => {
    navigate(`${PROFILE + PAYMENT_DETAILS}/${payment.id}`);
  };

  const lastPaymentElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading || isFetchingNextPage) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        },
        {
          threshold: 0.1,
          rootMargin: '100px',
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  if (isLoading && paymentList.length === 0) {
    return (
      <Box sx={{ bgcolor: 'grey.50', px: 2, py: 8, minHeight: '60vh' }}>
        {[...Array(4)].map((_, idx) => (
          <Box key={idx} mb={2}>
            <Skeleton
              variant="rectangular"
              height={80}
              sx={{ borderRadius: 3, mb: 1 }}
            />
            <Skeleton variant="text" width="60%" height={28} />
            <Skeleton variant="text" width="40%" height={24} />
          </Box>
        ))}
      </Box>
    );
  }

  if (isError) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography color="error">
            Failed to load payment history. Please try again.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (paymentList.length === 0) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography variant="body1" color="textSecondary">
            No payment history found.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      {paymentList.map((payment, index) => {
        const isLastPayment = index === paymentList.length - 1;

        return (
          <div
            key={`${payment.id}-${index}`}
            ref={isLastPayment ? lastPaymentElementRef : null}
          >
            <PaymentHistoryCard
              title={payment.description}
              amount={
                payment.amount && !isNaN(Number(payment.amount))
                  ? `£${Number(payment.amount).toFixed(2)}`
                  : 'No amount available'
              }
              status={payment.status}
              onClick={() =>
                handlePaymentClick({
                  id: payment.id,
                  description: payment.description,
                  amount: payment.amount,
                  status: payment.status,
                })
              }
            />
          </div>
        );
      })}

      {isFetchingNextPage && (
        <Box display="flex" flexDirection="column" alignItems="center" py={2}>
          <PaymentHistoryCardSkeleton />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Loading more payments...
          </Typography>
        </Box>
      )}

      {hasNextPage && !isFetchingNextPage && (
        <Box display="flex" justifyContent="center" alignItems="center" py={2}>
          <Button
            variant="outlined"
            onClick={() => {
              console.log('Manual load more clicked');
              fetchNextPage();
            }}
            disabled={isFetchingNextPage}
          >
            Load More ({paymentList.length} of {totalRecords})
          </Button>
        </Box>
      )}

      {!hasNextPage && paymentList.length > 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" py={3}>
          <Typography variant="body2" color="textSecondary">
            You've reached the end of your payment history ({paymentList.length}{' '}
            total)
          </Typography>
        </Box>
      )}

      <div ref={loadMoreRef} style={{ height: '1px' }} />
    </Container>
  );
};

export default PaymentHistoryListingComponent;
